import React, { useState } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Link } from 'react-router-dom';
import { Server, Network, Shield, Cpu, Layers, CheckCircle2, Zap, Lock, Database, Activity } from 'lucide-react';
import { useConsultationModal } from '../context/ConsultationModalContext';
import ArchitectureMap, { SimulationState } from '../components/ArchitectureMap';
import ZoneCard from '../components/ZoneCard';
import CapacityVisuals from '../components/CapacityVisuals';
import { useTheme } from '../context/ThemeContext';

// Zone configuration data for ZoneCards
const zoneConfigs = [
  {
    id: 'az-1',
    name: 'KATHMANDU (AZ-1)',
    role: 'Primary DC',
    color: 'emerald',
    status: 'active',
    description: 'Our primary data center in Kathmandu serves as the main hub for all production workloads with ultra-low latency access.',
    features: [
      { text: '<1ms Local Latency (NPIX)' },
      { text: '99.9% Uptime SLA Guaranteed' },
      { text: 'Carrier Neutral Connectivity' },
      { text: 'Tier III Aligned Facility' },
      { text: '24/7 Physical & Cyber Security' }
    ]
  },
  {
    id: 'az-2',
    name: 'LALITPUR (AZ-2)',
    role: 'Near DR',
    color: 'amber',
    status: 'standby',
    description: 'Synchronous replication ensures zero data loss. Located just 5km away but on separate infrastructure.',
    features: [
      { text: 'Zero Data Loss (RPO=0)' },
      { text: 'Synchronous Real-time Replication' },
      { text: 'Dark Fiber Ring Connected' },
      { text: 'Active-Active Load Balancing Ready' },
      { text: '5km Aerial Separation from Primary' }
    ]
  },
  {
    id: 'az-3',
    name: 'BUTWAL (AZ-3)',
    role: 'Far DR',
    color: 'blue',
    status: 'standby',
    description: 'Geographic separation across seismic zones ensures business continuity even during regional disasters.',
    features: [
      { text: 'RTO < 15 Minutes Guaranteed' },
      { text: 'Distinct Tectonic/Seismic Zone' },
      { text: '250km+ Geographic Separation' },
      { text: 'Asynchronous Data Replication' },
      { text: 'Air-Gapped Backup & Recovery' }
    ]
  }
];

const Platform = () => {
  const { openModal } = useConsultationModal();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [simulationState, setSimulationState] = useState(SimulationState.NORMAL);

  // Helper to determine zone status based on simulation
  const getZoneStatus = (zoneId) => {
    if (simulationState === SimulationState.KTM_FAILURE && zoneId === 'az-1') return 'offline';
    if (simulationState === SimulationState.REGIONAL_FAILURE && (zoneId === 'az-1' || zoneId === 'az-2')) return 'offline';
    return 'active';
  };
  
  return (
    <Layout>
      <SEO page="platform" />
      <PageHeader
        title="Sovereign by Design."
        highlight="Resilient by Architecture."
        description="Built on world-class infrastructure and operated entirely within Nepal, our platform delivers predictable performance, uncompromising security, and true data sovereignty—without reliance on foreign control planes or jurisdictions." />

      <section className="-mt-8 pb-4 bg-white dark:bg-slate-950 transition-colors duration-500">
        <div className="container mx-auto px-4">
            
            {/* 2. MULTI-AZ ARCHITECTURE - Animated Version from Home */}
            <section className="py-12 mb-20 bg-slate-50 dark:bg-[#020617] rounded-3xl border border-slate-200 dark:border-white/5 relative overflow-hidden transition-colors duration-500">
        
                {/* SVG Connection Layer - CONNECTING THE BOXES */}
                <div className="absolute inset-0 pointer-events-none z-0 hidden md:block">
                    <svg className="w-full h-full" preserveAspectRatio="none">
                        <defs>
                            <linearGradient id="syncFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="transparent" />
                                <stop offset="50%" stopColor="#eab308" />
                                <stop offset="100%" stopColor="transparent" />
                            </linearGradient>
                            <linearGradient id="asyncFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="transparent" />
                                <stop offset="50%" stopColor="#3b82f6" />
                                <stop offset="100%" stopColor="transparent" />
                            </linearGradient>
                        </defs>
                        
                        {/* Connection Line: KTM (Col 1) <-> LAL (Col 2) */}
                        <path 
                            d="M 17% 55% L 50% 55%" 
                            stroke="currentColor"
                            className="text-slate-300 dark:text-white/10"
                            strokeWidth="3" 
                            strokeDasharray="6 6" 
                            fill="none" 
                        />
                        
                        {/* Connection Line: LAL (Col 2) -> BTL (Col 3) */}
                        <path 
                            d="M 50% 55% L 83% 55%" 
                            stroke="currentColor"
                            className="text-slate-300 dark:text-white/10" 
                            strokeWidth="3" 
                            strokeDasharray="6 6" 
                            fill="none" 
                        />

                        {/* Animated Packets: KTM <-> LAL (Sync - Rapid) */}
                        <circle r="6" fill="#eab308" filter="url(#glow)">
                            <animateMotion dur="1.5s" repeatCount="indefinite" path="M 17% 55% L 50% 55%" />
                        </circle>
                        <circle r="6" fill="#eab308" filter="url(#glow)">
                            <animateMotion dur="1.5s" repeatCount="indefinite" path="M 50% 55% L 17% 55%" />
                        </circle>

                        {/* Animated Packets: LAL -> BTL (Async - Slower) */}
                        <circle r="6" fill="#3b82f6" filter="url(#glow)">
                            <animateMotion dur="3s" repeatCount="indefinite" path="M 50% 55% L 83% 55%" />
                        </circle>

                        {/* Filters for Glow */}
                        <filter id="glow">
                            <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </svg>
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="mb-12 text-center">
                        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">Designed for Business Continuity</h2>
                        <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">Even During Disasters. Your workloads are continuously protected across three geographically separated sites, each purpose-built for resilience.</p>
                    </div>

                    {/* Interactive Architecture Map Component */}
                    <div className="mb-8">
                        <ArchitectureMap isDarkMode={isDarkMode} simulationState={simulationState} />
                    </div>

                    {/* Failover Simulation Controls */}
                    <div className={`flex flex-wrap justify-center gap-3 mb-8 p-4 rounded-xl border backdrop-blur-md ${
                        isDarkMode ? 'bg-slate-900/80 border-slate-700' : 'bg-white/80 border-slate-200'
                    }`}>
                        <button
                            onClick={() => setSimulationState(SimulationState.NORMAL)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                simulationState === SimulationState.NORMAL
                                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                                    : isDarkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            Normal Operations
                        </button>
                        <button
                            onClick={() => setSimulationState(SimulationState.KTM_FAILURE)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                simulationState === SimulationState.KTM_FAILURE
                                    ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/25'
                                    : isDarkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            Simulate Kathmandu Failure
                        </button>
                        <button
                            onClick={() => setSimulationState(SimulationState.REGIONAL_FAILURE)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                simulationState === SimulationState.REGIONAL_FAILURE
                                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/25'
                                    : isDarkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            Simulate Regional Failure
                        </button>
                    </div>

                    {/* Zone Cards Grid */}
                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8 relative">
                        {zoneConfigs.map((zone) => (
                            <ZoneCard
                                key={zone.id}
                                zone={zone}
                                isActive={getZoneStatus(zone.id) === 'active'}
                                isOffline={getZoneStatus(zone.id) === 'offline'}
                                isDarkMode={isDarkMode}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* 3. CORE CAPABILITIES */}
            <div className="mb-32">
                <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-16 text-center">Core Platform Capabilities</h2>
                <div className="grid md:grid-cols-3 gap-8">
                    <FeatureBlock
                icon={<Server />}
                title="Compute & Storage"
                desc="High-density enterprise compute and NVMe-optimized storage for high IOPS workloads. Fault-tolerant distributed storage fabric with automated healing." />

                    <FeatureBlock
                icon={<Network />}
                title="Secure Networking"
                desc="Low-latency data center fabric with segmentation and policy-based isolation. Encrypted east-west traffic and DDoS-aware perimeter design." />

                    <FeatureBlock
                icon={<Cpu />}
                title="Kubernetes Platform"
                desc="Production-grade Kubernetes with multi-cluster architecture, central policy enforcement, and integrated ingress/load balancing." />

                    <FeatureBlock
                icon={<Database />}
                title="Database Platform"
                desc="Full lifecycle database service with automated patching, version management, cloning, and backup policies for rapid refresh cycles." />

                     <FeatureBlock
                icon={<Activity />}
                title="Managed SOC"
                desc="24/7 Security Operations Center with continuous monitoring, threat detection, and compliance reporting." />

                     <FeatureBlock
                icon={<Lock />}
                title="Compliance Ready"
                desc="Pre-configured controls for BFSI and government audit readiness, including role-based access and approval workflows." />

                </div>
                
                {/* Infrastructure Scale Visuals */}
                <CapacityVisuals isDarkMode={isDarkMode} />
            </div>

            {/* 4. AI & GPU INFRASTRUCTURE */}
            <section className="mb-32 bg-slate-50 dark:bg-slate-900/50 rounded-3xl p-12 border border-slate-200 dark:border-white/5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-[100px] pointer-events-none"></div>
                <div className="relative z-10 grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <Badge className="mb-6 bg-accent/10 text-accent hover:bg-accent/20 border-accent/20">AI-Ready Infrastructure</Badge>
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6">Secure AI for Regulated Enterprises</h2>
                        <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                            EverestCloud provides isolated high-performance infrastructure for AI and analytics workloads where data privacy is non-negotiable.
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-center gap-3 text-slate-700 dark:text-slate-300">
                                <Zap className="h-5 w-5 text-accent" /> Dedicated GPU Cluster
                            </li>
                            <li className="flex items-center gap-3 text-slate-700 dark:text-slate-300">
                                <Lock className="h-5 w-5 text-accent" /> Secure tenancy and segmented networks
                            </li>
                            <li className="flex items-center gap-3 text-slate-700 dark:text-slate-300">
                                <Activity className="h-5 w-5 text-accent" /> Ideal for risk modeling, fraud detection, and regulated analytics
                            </li>
                        </ul>
                    </div>
                    <div className="bg-white dark:bg-slate-950 p-8 rounded-2xl border border-slate-200 dark:border-white/10 shadow-xl">
                        <div className="flex items-center gap-4 mb-6">
                            <Cpu className="h-10 w-10 text-accent" />
                            <div>
                                <h4 className="font-bold text-lg text-slate-900 dark:text-white">GPU Compute</h4>
                                <p className="text-sm text-slate-500">H100 / RTX Pro</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                <div className="h-full w-[35%] bg-accent rounded-full"></div>
                            </div>
                            <div className="flex justify-between text-sm text-slate-500">
                                <span>Compute Load</span>
                                <span>35% Utilization</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. CORE TECH STACK */}
            <div className="mb-32">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">Core Technology Stack</h2>
                    <p className="text-lg text-slate-600 dark:text-slate-400">Transparent. Proven. Enterprise-Grade. We disclose our stack because transparency builds trust.</p>
                </div>
                
                <div className="grid md:grid-cols-4 gap-6">
                    <StackCard title="Compute" subtitle="High-Density Enterprise Servers" items={["Modern x86 architecture", "NVMe-optimized performance", "GPU-ready for AI"]} />
                    <StackCard title="Virtualization" subtitle="Enterprise Cloud Fabric" items={["Self-healing architecture", "Distributed storage", "Automated lifecycle management"]} />
                    <StackCard title="Networking" subtitle="Low-Latency Fabric" items={["High-throughput switching", "East-west optimization", "Micro-segmented networking"]} />
                    <StackCard title="Security" subtitle="Defense-in-Depth" items={["Zero Trust architecture", "Privilege Access Management", "Continuous monitoring"]} />
                </div>
            </div>

            {/* 6. PLATFORM PRINCIPLES */}
            <div className="grid md:grid-cols-3 gap-8 mb-32">
                <StackCard
                    title="Sovereign Control Plane"
                    subtitle="Local Orchestration"
                    items={["EverestCloud's management and orchestration layers are hosted entirely within Nepal.", "No foreign dependencies for provisioning, monitoring, or controlling your infrastructure."]} 
                />
                <StackCard
                    title="Dedicated Resources"
                    subtitle="Guaranteed Performance"
                    items={["We do not oversubscribe memory or storage without explicit disclosure.", "What you provision is what you get—guaranteed and auditable."]} 
                />
                <StackCard
                    title="Predictable Performance"
                    subtitle="Mission Critical"
                    items={["Designed for latency-sensitive workloads like Core Banking, AI & Analytics, and Telecom OSS/BSS.", "Consistent low-latency access."]} 
                />
            </div>

            {/* 7. WHY EVERESTCLOUD (MATCHING HOME PAGE) */}
            <section className="py-24 bg-white dark:bg-slate-950">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white text-center mb-16">Why Leading Institutions Choose EverestCloud</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <ReasonCard text="Built specifically for Nepal’s regulatory environment" />
                        <ReasonCard text="Designed for banks, government, and enterprises" />
                        <ReasonCard text="Operated by enterprise infrastructure experts" />
                        <ReasonCard text="No dependency on foreign jurisdictions" />
                        <ReasonCard text="Local accountability and legal recourse" />
                        <ReasonCard text="Transparent, predictable pricing in NPR" />
                    </div>
                </div>
            </section>

            {/* 8. FINAL CTA */}
            <div className="mt-16 text-center">
                <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">Ready to See the Architecture?</h2>
                <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-10">
                    Request a confidential architecture walkthrough and see how EverestCloud protects your most critical workloads—by design.
                </p>
                <Button 
                    size="xl" 
                    className="h-14 px-10 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 transition-all rounded-full shadow-lg"
                    onClick={() => openModal('architecture_walkthrough')}
                >
                    Request Walkthrough
                </Button>
            </div>

        </div>
      </section>
    </Layout>);

};


// Sub-components
const ZoneBullet = ({ text }) => (
    <li className="flex items-start gap-3 text-slate-600 dark:text-slate-300 text-xs font-medium">
        <CheckCircle2 className="h-3.5 w-3.5 text-accent/70 shrink-0 mt-0.5" />
        <span className="leading-snug">{text}</span>
    </li>
);

const FeatureBlock = ({ icon, title, desc }) =>
<div className="p-6 border-l-2 border-slate-200 dark:border-slate-800 hover:border-accent pl-6 transition-colors group">
        <div className="mb-4 text-slate-400 dark:text-slate-500 group-hover:text-accent transition-colors">{icon}</div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{title}</h3>
        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{desc}</p>
    </div>;


const StackCard = ({ title, subtitle, items }) =>
<div className="p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-white/10 shadow-sm">
        <h4 className="dark:text-white !font-bold !text-lg !text-center mb-1 text-slate-900">{title}</h4>
        <p className="uppercase !font-bold !text-xs !text-center mb-4 text-accent">{subtitle}</p>
        <ul className="space-y-2">
            {items.map((item, i) =>
    <li key={i} className="dark:text-slate-400 !text-sm !text-center text-slate-600">• {item}</li>
    )}
        </ul>
    </div>;


const PrincipleCard = ({ title, desc }) =>
<div className="p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-white/10 shadow-sm">
        <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4">{title}</h4>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{desc}</p>
    </div>;


const ReasonCard = ({ text }) =>
<div className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
        <CheckCircle2 className="h-6 w-6 text-accent shrink-0 mt-0.5" />
        <p className="text-lg text-slate-700 dark:text-slate-300 font-light">{text}</p>
    </div>;


const WhyPoint = ({ text }) =>
<div className="flex items-start gap-3">
        <CheckCircle2 className="h-6 w-6 text-accent shrink-0" />
        <p className="text-lg text-slate-200 font-light">{text}</p>
    </div>;


export default Platform;